import React, { useState, useEffect } from "react";
import { db, storage } from "../../firebaseConfig";
import { collection, doc, getDoc, addDoc, updateDoc, deleteDoc, Timestamp } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";

const EditAndApproveForm = ({ listingID, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [carData, setCarData] = useState(null);
  const [pictures, setPictures] = useState([]);
  const [documents, setDocuments] = useState({
    insurance: { file: null, expiry: null, uploaded: false },
    registration: { file: null, expiry: null, uploaded: false },
    LLC: { file: null, expiry: null, uploaded: false },
  });
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    const fetchListing = async () => {
      try {
        const listingRef = doc(db, "listings", listingID);
        const listingSnap = await getDoc(listingRef);
        if (listingSnap.exists()) {
          setCarData(listingSnap.data());
        } else {
          console.error("Listing not found");
        }
      } catch (error) {
        console.error("Error fetching listing:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchListing();
  }, [listingID]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCarData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePictureUpload = (e) => {
    setPictures([...e.target.files]);
  };

  const handleFileUpload = async (file, fieldName) => {
    if (!carData || !carData.carName) return;
    const fileRef = ref(storage, `cars/${carData.carName}/${fieldName}`);
    const uploadTask = uploadBytesResumable(fileRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(progress);
      },
      (error) => console.error("Upload failed:", error),
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        setDocuments((prev) => ({
          ...prev,
          [fieldName]: { ...prev[fieldName], file: url, uploaded: true },
        }));
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!carData) return;

    try {
      const pictureURLs = [];
      for (const picture of pictures) {
        const pictureRef = ref(storage, `cars/${carData.carName}/${picture.name}`);
        const uploadTask = await uploadBytesResumable(pictureRef, picture);
        const downloadURL = await getDownloadURL(uploadTask.ref);
        pictureURLs.push(downloadURL);
      }

      const formattedData = {
        ...carData,
        year: parseInt(carData.year, 10),
        mileage: parseInt(carData.mileage, 10),
        totalValue: parseFloat(carData.totalValue),
        freeDays: parseInt(carData.freeDays, 10),
        freePercent: parseFloat(carData.freePercent),
        predictedROI: parseFloat(carData.predictedROI),
        maintenanceCost: parseFloat(carData.maintenanceCost),
        insuranceCost: parseFloat(carData.insuranceCost),
        isAvailable: true,
        freeDaysRatio: `${carData.freeDays} days per ${carData.freePercent}%`,
        tags: carData.tags || [],
        carType: carData.carType || [],
        pictures: [...(carData.pictures || []), ...pictureURLs],
        documents: Object.entries(documents).reduce(
          (acc, [key, val]) => (val.uploaded ? { ...acc, [key]: val } : acc),
          {}
        ),
        investors: carData.investors ? [...carData.investors, carData.userID] : [carData.userID],
        createdAt: Timestamp.now(),
      };

      // Add the car document to the "cars" collection
      const carDocRef = await addDoc(collection(db, "cars"), formattedData);
      await updateDoc(carDocRef, { carID: carDocRef.id });

      // Create an investment document
      const investmentData = {
        amount: parseFloat(carData.initialInvestment), // Map the initial investment to the amount
        carID: carDocRef.id,
        date: Timestamp.now(),
        userID: carData.userID,
        ownershipPercentage: (parseFloat(carData.initialInvestment) / parseFloat(carData.totalValue)) * 100,
      };
      const investmentDocRef = await addDoc(collection(db, "investments"), investmentData);

      // Update the investmentID field with the document ID
      await updateDoc(investmentDocRef, { investmentID: investmentDocRef.id });

      // Delete the original listing
      const listingRef = doc(db, "listings", listingID);
      await deleteDoc(listingRef);

      alert("Listing approved, car added, and investment recorded!");
      onClose();
    } catch (error) {
      console.error("Error approving listing:", error);
      alert("Failed to approve listing. Please try again.");
    }
  };

  if (loading) return <p>Loading...</p>;
  if (!carData) return <p>Listing not found</p>;

  return (
    <form onSubmit={handleSubmit} style={{ padding: "20px" }}>
      <h2>Edit and Approve Listing</h2>

      {/* Render multiple input fields to allow editing all details */}
      <div>
        <label>Car Name:</label>
        <input
          type="text"
          name="carName"
          value={carData.carName || ""}
          onChange={handleInputChange}
          required
        />
      </div>

      <div>
        <label>Year:</label>
        <input
          type="number"
          name="year"
          value={carData.year || ""}
          onChange={handleInputChange}
          required
        />
      </div>

      <div>
        <label>Mileage:</label>
        <input
          type="number"
          name="mileage"
          value={carData.mileage || ""}
          onChange={handleInputChange}
          required
        />
      </div>

      <div>
        <label>Total Value (Offering Price):</label>
        <input
          type="number"
          name="totalValue"
          value={carData.totalValue || ""}
          onChange={handleInputChange}
          required
        />
      </div>

      <div>
        <label>Minimum Investment:</label>
        <input
          type="number"
          name="minimumInvestment"
          value={carData.minimumInvestment || ""}
          onChange={handleInputChange}
          required
        />
      </div>

      <div>
        <label>Upload Pictures:</label>
        <input type="file" multiple onChange={handlePictureUpload} />
      </div>

      <div>
        <label>Documents:</label>
        {["insurance", "registration", "LLC"].map((docField) => (
          <div key={docField}>
            <label>
              {docField.charAt(0).toUpperCase() + docField.slice(1)}:
              <input
                type="file"
                onChange={(e) =>
                  setDocuments((prev) => ({
                    ...prev,
                    [docField]: { ...prev[docField], file: e.target.files[0] },
                  }))
                }
              />
              <input
                type="date"
                onChange={(e) =>
                  setDocuments((prev) => ({
                    ...prev,
                    [docField]: { ...prev[docField], expiry: e.target.value },
                  }))
                }
              />
            </label>
          </div>
        ))}
      </div>

      <button type="submit">Approve Listing</button>
    </form>
  );
};

export default EditAndApproveForm;
